import { DownloadOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Button, Dropdown, Tag, Tooltip } from 'antd';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { InfoBlock } from 'app/components/commons/InfoBlock/InfoBlock';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGeneratePaymentReceipt } from 'app/hooks/data/core/admin/bookings/useGeneratePaymentReceipt';
import { useGetPaymentReceipt } from 'app/hooks/data/core/admin/bookings/useGetPaymentReceipt';
import type { BookingDetails } from 'app/typings/core/admin/bookings/booking';
import { BOOKING_STATUS } from 'app/typings/core/admin/bookings/enum/bookingStatus';
import { isFuture } from 'app/utils/dayjs';
import { capitalizeFirstLetter } from 'app/utils/string';

import { formatPaymentStatus } from './utils';

type Props = {
  bookingDetails: BookingDetails;
  onCancelBooking: () => void;
  onOpenRefundModal: () => void;
  onOpenVoucherModal: () => void;
};

export const UserPayment = ({
  bookingDetails,
  onCancelBooking,
  onOpenRefundModal,
  onOpenVoucherModal,
}: Props) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { enterEditMode } = useContext(ViewEditModeContext);

  const isDownloadDisabled =
    !bookingDetails.payments[0]?.paymentReceiptPath ||
    isFuture(new Date(bookingDetails.checkOut));
  const isGenerateDisabled = isFuture(new Date(bookingDetails.checkOut));
  const isBookingCanceled = bookingDetails.status === BOOKING_STATUS.CANCELED;

  const { mutate: downloadReceipt, isLoading: isDownloading } =
    useGetPaymentReceipt(bookingDetails.code);

  const { mutate: generateReceipt, isLoading: isGenerating } =
    useGeneratePaymentReceipt(bookingDetails.code);

  const handleRefund = () => {
    onOpenRefundModal();
    enterEditMode();
  };

  const buttonDownloadReceipt = () => {
    const renderButton = () => (
      <Button
        className="button"
        icon={<DownloadOutlined />}
        disabled={isDownloadDisabled}
        loading={isDownloading}
        onClick={() => {
          if (!isDownloadDisabled) {
            downloadReceipt();
          }
        }}
      >
        Receipt
      </Button>
    );

    return isDownloadDisabled ? (
      <Tooltip
        placement="top"
        title={
          isGenerateDisabled
            ? 'The payment receipt will be accessible after the user has checked out.'
            : 'You have to manually generate receipt for booking before 2024'
        }
      >
        {renderButton()}
      </Tooltip>
    ) : (
      renderButton()
    );
  };

  const items = [
    {
      key: 'generate',
      label: 'Generate new receipt',
      onClick: async () => generateReceipt(),
      disabled: isGenerateDisabled,
      isLoading: isGenerating,
    },
    {
      key: 'cancelBooking',
      label: 'Cancel booking',
      onClick: onCancelBooking,
      disabled: isBookingCanceled,
    },
  ];

  const { label: paymentLabel, color: paymentColor } = formatPaymentStatus(
    bookingDetails.payments[0].status
  );

  const hasGiftCardPayment =
    bookingDetails.payments[0].giftCardAmount > 0 &&
    bookingDetails.payments[0].giftCardId;

  return (
    <InfoBlock
      title="User payment"
      actions={
        <>
          <Button onClick={onOpenVoucherModal}>Voucher</Button>
          <Button onClick={handleRefund}>Refund</Button>
          {buttonDownloadReceipt()}
          <Dropdown
            trigger={['click']}
            menu={{ items }}
            getPopupContainer={(node) =>
              (node.parentNode?.parentNode || node) as HTMLElement
            }
          >
            <Button className="icon-btn">
              <EllipsisOutlined className="icon" size={16} />
            </Button>
          </Dropdown>
        </>
      }
      rows={[
        {
          label: 'Payment status',
          value: <Tag color={paymentColor}>{paymentLabel}</Tag>,
        },
        {
          label: 'Payment provider',
          value: capitalizeFirstLetter(bookingDetails.payments[0].psp),
        },
        {
          label: 'Card info',
          value: bookingDetails.payments[0].cardLast4
            ? `•••• ${bookingDetails.payments[0].cardLast4}`
            : '-',
        },
        {
          label: 'Credit card amount',
          value: currencyFormatter(bookingDetails.payments[0].creditCardAmount),
        },
        ...(hasGiftCardPayment
          ? [
              {
                label: 'Gift card code',
                value: bookingDetails.payments[0].giftCardId || '-',
              },
              {
                label: 'Gift card amount',
                value: currencyFormatter(
                  bookingDetails.payments[0].giftCardAmount
                ),
              },
            ]
          : []),
        ...(bookingDetails.payments[0].creditAmount > 0
          ? [
              {
                label: 'Credit amount',
                value: currencyFormatter(
                  bookingDetails.payments[0].creditAmount
                ),
              },
            ]
          : []),
        ...(bookingDetails.payments[0].refusalReason
          ? [
              {
                label: 'Refusal reason',
                value: bookingDetails.payments[0].refusalReason,
              },
            ]
          : []),
      ]}
    />
  );
};
